import { useEffect, useState } from 'react'

import { t } from 'commons/libs/i18n'
import { padZeros } from 'commons/utils/string'
import {
  Badge,
  Flex,
  type HeadItemType,
  Icon,
  NotFound,
  SlimButton,
  Table,
  type TableProps,
  type TableSubRow,
  Text,
} from 'components'
import { PaginationV2 } from 'components/Table/PaginationV2'
import type { EdiRequest } from 'constants/enums'
import { EdiStatus } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import { getEdiReprocessCode } from 'container/EdiManagement/helpers'
import * as S from 'container/EdiManagement/styles'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { fetchEdiRequestLogs, postReprocessEdiOrder } from 'services/ediManagement'
import { theme } from 'styles/theme'

import { ReprocessModal } from '../ReprocessModal'
import { LegalName } from './components/LegalName'
import { StatusBadge } from './components/StatusBadge'
import { SubrowItem } from './components/SubrowItem'

const RECOMMENDED_SOLUTION_FILE_NAME = 'gestao_edi_solucao_recomendada.pdf'

interface EdiTableProps extends Pick<TableProps, 'isLoading'> {
  currentPage: number
  data: EdiRequest[]
  onClearFilter: () => void
  onItemsPerPageChange: (items: number) => void
  onPageChange: (page: number) => void
  onReprocessSuccess: () => void | Promise<void>
  onSelectionChange: (selectedEdiRequests: EdiRequest[]) => void
  pageSize: number
  totalItems: number
}

export const EdiTable = ({
  currentPage,
  data,
  isLoading,
  onClearFilter,
  onItemsPerPageChange,
  onPageChange,
  onReprocessSuccess,
  onSelectionChange,
  pageSize,
  totalItems,
}: EdiTableProps) => {
  const navigate = useNavigate()

  const [isLoadingItem, setIsLoadingItem] = useState<boolean>(!!isLoading)
  const [ediRequests, setEdiRequests] = useState<EdiRequest[]>(data)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isReprocessing, setIsReprocessing] = useState<boolean>(false)
  const [selectedEdiRequestId, setSelectedEdiRequestId] = useState<string>('')

  useEffect(() => {
    setEdiRequests(data)
  }, [data])

  const tableLabels: HeadItemType[] = [
    { columnName: 'status', label: t('screens.edi.table.status') },
    { columnName: 'id', label: t('screens.edi.table.ediRequestId') },
    { columnName: 'customerOrder', label: t('screens.edi.table.customerOrder') },
    { columnName: 'legalName', label: t('screens.edi.table.legalName') },
    { columnName: 'requestedAt', label: t('screens.edi.table.requestedAt') },
  ]

  const tableData = () => {
    return ediRequests.map((item) => {
      const requestedAt = `${item.formattedDate} ${t('common.at')} ${item.formattedTime}`

      return {
        checked: !!item.checked,
        content: [
          <StatusBadge key={item.id} status={item.status} />,
          padZeros(item.id, 4), // NOTE: W.I.P - Confirm with the team what will be done with this field in the future
          item.customerOrder,
          <LegalName key={`legal-${item.id}`} legalName={item.legalName} sapCode={item.sapCode} />,
          requestedAt,
        ],
        disableCheckbox: item.status !== EdiStatus.FAIL,
        expanded: !!item.expanded,
        id: item.id,
        subRowFlex: true,
        subRows: tableSubrows(item),
      }
    })
  }

  const tableSubrows = (item: EdiRequest): TableSubRow[] | undefined => {
    const content: React.ReactNode = (
      <Flex
        direction="column"
        gap="md"
        key={`flex-${item.id}`}
        style={{ margin: theme.spacing.ion.medium }}
      >
        {renderErrors(item)}

        <Flex alignItems="center" gap="md">
          {item.status == EdiStatus.PROCESSED ? (
            <Flex direction="column" fullWidth style={{ gap: theme.spacing.ion.medium }}>
              <Badge appearance="soft" color="success">
                <Flex alignItems="center" style={{ gap: theme.spacing.ion.xsmall2 }}>
                  <Icon color={theme.colors.ion.success.onsoft} icon="circle-check" size="12" />
                  {t('screens.edi.orderProcessedSuccess')}
                </Flex>
              </Badge>
              {/* NOTE: W.I.P - Maybe it's will be an array of orders */}
              {item.orderNumber && (
                <SlimButton
                  content={t('screens.edi.viewOrder')}
                  icon={<Icon icon="arrow-right" size="16" viewBox="0 0 24 24" />}
                  iconPosition="right"
                  onClick={() => handleOpenOrder(item.orderNumber!)}
                  variant="primary"
                />
              )}
            </Flex>
          ) : (
            <>
              <SlimButton
                content={t('screens.edi.subrowItem.reprocessButton')}
                disabled={isLoadingItem || isReprocessing || item.status !== EdiStatus.FAIL}
                icon={<Icon icon="refresh-cw" size="16" />}
                loading={isReprocessing && selectedEdiRequestId === item.id}
                onClick={() => handleReprocess(item.id)}
                variant="primary"
              />
              <SlimButton
                content={t('screens.edi.subrowItem.recommendedSolutionButton')}
                disabled={isLoadingItem}
                icon={<Icon icon="download" size="16" />}
                onClick={handleOpenRecommendedSolution}
                variant="ghost"
              />
            </>
          )}
        </Flex>
      </Flex>
    )
    return [
      {
        content,
        id: item.id,
      },
    ]
  }

  const renderErrors = (item: EdiRequest) => {
    if (item.status === EdiStatus.PROCESSED || !item.logs || item.logs.length === 0) return null

    return (
      <S.ErrorBox>
        <Text customColor={theme.colors.ion.danger.strong} size="2xs" weight="md">
          {t('screens.edi.subrowItem.errorIdentified')}:
        </Text>

        {item.logs?.map((log, index) => (
          <SubrowItem key={`${item.id}-${index}`} log={log} />
        ))}
      </S.ErrorBox>
    )
  }

  const handleGetEdiRequestsLogs = async (ediRequestId: string) => {
    setIsLoadingItem(true)

    const selectedItem = ediRequests.find((item) => item.id === ediRequestId)

    if (!selectedItem) {
      setIsLoadingItem(false)
      return
    }

    const isItemExpanded = selectedItem.expanded

    if (isItemExpanded) {
      const newEdiRequestsList = ediRequests.map((item) => {
        if (item.id === ediRequestId) {
          return {
            ...item,
            expanded: !item?.expanded,
            hasAsyncSubRows: item.status === EdiStatus.FAIL,
          }
        }

        return item
      })

      setEdiRequests(newEdiRequestsList)
      setIsLoadingItem(false)
      return
    }

    if (selectedItem.status === EdiStatus.PROCESSED) {
      setEdiRequests((prev) =>
        prev.map((item) => (item.id === ediRequestId ? { ...item, expanded: true } : item)),
      )
      setIsLoadingItem(false)
      return
    }

    try {
      const logs = await fetchEdiRequestLogs(getEdiReprocessCode(selectedItem))

      setEdiRequests((prev) =>
        prev.map((item) =>
          item.id === ediRequestId ? { ...item, expanded: !item?.expanded, logs } : item,
        ),
      )
    } catch (_) {
      toast.error(t('common.errors.unableToFetchData'))
    } finally {
      setIsLoadingItem(false)
    }
  }

  const handleReprocess = (ediRequestId: string) => {
    setSelectedEdiRequestId(ediRequestId)
    setIsModalOpen(true)
  }

  const handleConfirmReprocess = async () => {
    const selectedItem = ediRequests.find((item) => item.id === selectedEdiRequestId)

    if (!selectedItem) return

    try {
      setIsReprocessing(true)

      await postReprocessEdiOrder(getEdiReprocessCode(selectedItem))

      toast.success(t('screens.edi.messages.reprocessRequestSuccess'))
      setIsModalOpen(false)
      await onReprocessSuccess()
    } catch (_) {
      toast.error(t('screens.edi.messages.reprocessRequestError'))
    } finally {
      setIsReprocessing(false)
      setSelectedEdiRequestId('')
    }
  }

  const handleOpenRecommendedSolution = () => {
    window.open(
      `${process.env.REACT_APP_DOWNLOAD_FILES_BASE_URL}/${RECOMMENDED_SOLUTION_FILE_NAME}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handleChecked = (checked: boolean, id?: string) => {
    if (!id) {
      const newEdiRequestsList = ediRequests.map((item) => {
        if (item.status !== EdiStatus.FAIL) return item
        return { ...item, checked }
      })

      // TODO: Dispatch an action to update the store with the checked status
      setEdiRequests(newEdiRequestsList)
      onSelectionChange(newEdiRequestsList.filter((item) => item.checked))
      return
    }

    const newEdiRequestsList = ediRequests.map((item) => {
      if (item.id === id && item.status === EdiStatus.FAIL) {
        return { ...item, checked: !item.checked }
      }

      return item
    })

    // TODO: Dispatch an action to update the store with the checked status
    setEdiRequests(newEdiRequestsList)
    onSelectionChange(newEdiRequestsList.filter((item) => item.checked))
    return
  }

  const handleOpenOrder = (order?: string) => {
    navigate(`${URL_CONST.MANAGE_ORDER}/${order}`)
  }

  const notFoundComponent = (
    <NotFound
      description={
        <Flex alignItems="center" direction="column" gap="xm">
          {t('common.messages.noResultFromFilters')}
          <SlimButton
            content={t('buttons.clearFilters')}
            icon={<Icon icon="close" size="18" />}
            onClick={onClearFilter}
            type="button"
            variant="info"
          />
        </Flex>
      }
      icon={<Icon icon="bot" size="40" />}
      title={t('common.messages.opsNotFound')}
    />
  )

  return (
    <>
      <S.TableWrapper>
        <Table
          bodyData={tableData()}
          checkboxSize="small"
          distributeWidth={true}
          hasAsyncExpand
          hasCheckbox
          headData={tableLabels}
          isLoading={isLoadingItem}
          notFoundComponent={notFoundComponent}
          rowHeight={48}
          setChecked={handleChecked}
          toggleExpand={handleGetEdiRequestsLogs}
        />
        <PaginationV2
          currentPage={currentPage}
          itemsPerPage={pageSize}
          itemsPerPageOptions={[20, 40, 60, 80, 100]}
          onItemsPerPageChange={onItemsPerPageChange}
          onPageChange={onPageChange}
          totalItems={totalItems}
        />
      </S.TableWrapper>
      <ReprocessModal
        loading={isReprocessing}
        onConfirm={handleConfirmReprocess}
        open={isModalOpen}
        setOpen={setIsModalOpen}
      />
    </>
  )
}
